import { useState } from "react"
import toast from "react-hot-toast"
import { lumi } from "@/lib/lumi"
import { useAuth } from "@/hooks/useAuth"
import { useCalculator } from "@/context/CalculatorContext"
import {Save, Printer, CheckCircle2, X} from 'lucide-react'

export default function ActionFooter() {
  const { isAuthenticated, user } = useAuth()
  const {
    ingredients,
    totalDoughWeight,
    totalTrueHydration,
    stages,
    formulaName,
    setFormulaName,
    bulkFermentationTime,
    finalProofTime,
  } = useCalculator()
  const [saving, setSaving] = useState(false)
  const [recipeName, setRecipeName] = useState(formulaName || "")
  const [showNameInput, setShowNameInput] = useState(false)

  const handleSave = async () => {
    if (!isAuthenticated) {
      lumi.auth.openPopup()
      return
    }
    setRecipeName(formulaName || "")
    setShowNameInput(true)
  }

  const confirmSave = async () => {
    if (!recipeName.trim()) {
      toast.error("Please enter a recipe name")
      return
    }
    if (!user) return

    setSaving(true)
    try {
      await lumi.entities.saved_formulas.create({
        userId: user.userId,
        recipeName: recipeName.trim(),
        totalDoughWeight,
        targetHydrationPercentage: totalTrueHydration,
        ingredientsJson: { ingredients, stages, bulkFermentationTime, finalProofTime },
        createdAt: new Date().toISOString(),
      })
      setFormulaName(recipeName.trim())
      toast.success("Formula saved to your account!", {
        style: {
          background: "#10B981",
          color: "white",
          fontWeight: "600",
        },
        iconTheme: { primary: "white", secondary: "#10B981" },
      })
      setShowNameInput(false)
    } catch (err) {
      console.error("Save failed:", err)
      toast.error("Failed to save formula. Please try again.")
    } finally {
      setSaving(false)
    }
  }

  const handlePrint = () => {
    window.print()
  }

  return (
    <div className="no-print mt-6 bg-white rounded-xl border border-gray-200 p-5 card-hover">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 bg-indigo-50 rounded-lg flex items-center justify-center flex-shrink-0">
          <CheckCircle2 size={17} className="text-indigo-600" strokeWidth={2.2} />
        </div>
        <div>
          <h2 className="text-base font-semibold text-gray-900 leading-tight">Finish Your Formula</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Save it to your account or print a copy for the kitchen
          </p>
        </div>
      </div>

      {showNameInput ? (
        <div className="animate-fade-up">
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
            Recipe Name
          </label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              autoFocus
              type="text"
              value={recipeName}
              onChange={(e) => setRecipeName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") confirmSave()
                if (e.key === "Escape") setShowNameInput(false)
              }}
              placeholder="e.g. Country Sourdough 75%..."
              className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-transparent transition-shadow"
            />
            <div className="flex gap-2">
              <button
                onClick={confirmSave}
                disabled={saving}
                className="flex-1 sm:flex-none bg-indigo-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-indigo-700 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-sm hover:shadow"
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                onClick={() => { setShowNameInput(false); setRecipeName("") }}
                aria-label="Cancel"
                className="flex items-center justify-center text-gray-500 px-3 py-2.5 rounded-lg hover:bg-gray-100 hover:text-gray-700 transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleSave}
              className="flex-1 flex items-center justify-center gap-2 bg-indigo-600 text-white text-sm font-semibold py-3 px-5 rounded-lg hover:bg-indigo-700 active:scale-[0.98] transition-all shadow-sm hover:shadow"
            >
              <Save size={16} />
              Save Formula to My Account
            </button>
            <button
              onClick={handlePrint}
              className="flex-1 flex items-center justify-center gap-2 bg-white text-gray-700 text-sm font-semibold py-3 px-5 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 active:scale-[0.98] transition-all"
            >
              <Printer size={16} />
              Print / Export Recipe to PDF
            </button>
          </div>
          {!isAuthenticated && (
            <p className="text-xs text-gray-400 mt-3 text-center sm:text-left">
              You will be asked to sign in before saving to your account.
            </p>
          )}
        </>
      )}
    </div>
  )
}
